import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DateTime } from 'luxon';
import { useForm, useStore } from '@tanstack/react-form';
import { DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Currency } from '../../../constants/currency';
import { Money } from '../../../utils/currency';
import { BillingItemType, BillingItem } from '../../../model/BillingItem';
import {
  useCreateBillingItemMutation,
  useUpdateBillingItemMutation,
  useBillingItem,
  useDeleteBillingItemMutation,
} from '../../../stores/FetcherStores/BillingItemFetcher';
import { ALL_SUBSCRIPTIONS, RECURRING_TYPES, USAGE_TYPES } from '../../../constants';

interface BillingItemFormProps {
  billingItemId?: string;
  open: boolean;
  onClose: () => void;
  companyId: string;
}

const BillingItemForm = observer(
  ({ billingItemId, open, onClose, companyId }: BillingItemFormProps): JSX.Element => {
    const { mutateAsync: createBillingItem, isLoading: isCreateLoading } =
      useCreateBillingItemMutation();
    const { mutateAsync: updateBillingItem, isLoading: isUpdateLoading } =
      useUpdateBillingItemMutation();
    const { mutateAsync: deleteBillingItem, isLoading: isDeleteLoading } =
      useDeleteBillingItemMutation();
    const { data: billingItem } = useBillingItem(companyId, billingItemId);
    const { t } = useTranslation();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const getEndDate = () => {
      if (
        billingItem?.type !== BillingItemType.SUBSCRIPTION &&
        billingItem?.type !== BillingItemType.RECURRING
      ) {
        return null;
      }
      if (!billingItem?.endDate) {
        return null;
      }
      const endDate = DateTime.fromISO(billingItem.endDate);
      const tenYearsFromNow = DateTime.now().plus({ years: 10 });
      return endDate > tenYearsFromNow ? null : endDate;
    };

    const form = useForm({
      defaultValues: {
        type: billingItem?.type || BillingItemType.SUBSCRIPTION,
        isDemo: billingItem?.isDemo ?? false,
        subscriptionId:
          billingItem?.type === BillingItemType.SUBSCRIPTION && billingItem?.subscriptionId
            ? billingItem.subscriptionId
            : '',
        recurringType:
          billingItem?.type === BillingItemType.RECURRING && billingItem?.recurringType
            ? billingItem.recurringType
            : '',
        startDate:
          billingItem?.type === BillingItemType.SUBSCRIPTION ||
          billingItem?.type === BillingItemType.RECURRING
            ? DateTime.fromISO(billingItem.startDate)
            : DateTime.now(),
        endDate: getEndDate(),
        paidUntil:
          (billingItem?.type === BillingItemType.RECURRING ||
            billingItem?.type === BillingItemType.SUBSCRIPTION) &&
          billingItem?.paidUntil
            ? DateTime.fromISO(billingItem.paidUntil)
            : null,
        usageType:
          billingItem?.type === BillingItemType.USAGE && billingItem?.usageType
            ? billingItem.usageType
            : '',
        datePaid:
          billingItem?.type === BillingItemType.ONE_TIME && billingItem?.datePaid
            ? billingItem.datePaid
            : '',
        reference:
          billingItem?.type === BillingItemType.RECURRING && billingItem?.reference
            ? billingItem.reference
            : '',
        period: billingItem?.type === BillingItemType.USAGE && billingItem?.period,
        quantity: billingItem?.quantity || 1,
        unitPrice: billingItem?.unitPrice?.asDecimal().toString(),
        currency: billingItem?.unitPrice?.currency ?? Currency.SEK,
        name: billingItem?.type === BillingItemType.ONE_TIME ? billingItem.name : '',
        taxRate: `${billingItem?.taxRate ?? 25}`,
        discount: `${billingItem?.discount ?? 0}`,
      },
      onSubmit: async ({ value }) => {
        const billingItemData: Omit<BillingItem, 'createdAt' | 'id' | 'createdBy'> = {
          type: value.type as BillingItemType,
          isDemo: value.isDemo,
          ...(value.type === BillingItemType.SUBSCRIPTION && {
            subscriptionId: value.subscriptionId,
            startDate: value.startDate ? value.startDate.toISO() : undefined,
            endDate: value.endDate ? value.endDate.toISO() : null,
            paidUntil: value.paidUntil ? value.paidUntil.toISO() : null,
          }),
          ...(value.type === BillingItemType.RECURRING && {
            recurringType: value.recurringType,
            reference: value.reference,
            startDate: value.startDate ? value.startDate.toISO() : undefined,
            endDate: value.endDate ? value.endDate.toISO() : null,
            paidUntil: value.paidUntil ? value.paidUntil.toISO() : null,
          }),
          ...(value.type === BillingItemType.USAGE && {
            usageType: value.usageType,
            reference: value.reference,
            period: value.period,
          }),
          ...(value.type === BillingItemType.ONE_TIME && {
            datePaid: value.datePaid,
            name: value.name,
          }),
          quantity: value.quantity,
          unitPrice: new Money(parseFloat(value.unitPrice ?? '0') * 100, value.currency),
          discount: parseFloat(value.discount),
          taxRate: parseInt(value.taxRate, 10),
        };

        if (billingItem?.id) {
          await updateBillingItem(companyId, billingItem.id, billingItemData)
            .then(() => {
              onClose();
            })
            .catch(() => {});
        } else {
          await createBillingItem(companyId, billingItemData)
            .then(() => {
              onClose();
            })
            .catch(() => {});
        }
      },
    });

    useEffect(() => {
      form.reset();
    }, [billingItemId]);

    const type = useStore(form.store, (state) => state.values.type);
    const isLoading = isCreateLoading || isUpdateLoading || isDeleteLoading;

    return (
      <>
        <Drawer anchor="right" open={open} onClose={onClose}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1',
              width: 600,
              height: '100%',
              paddingRight: 4,
              paddingLeft: 4,
            }}
          >
            <Toolbar />
            <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={onClose}>
                <ChevronRightIcon />
              </IconButton>
            </Toolbar>
            <Typography variant="h4" paragraph gutterBottom>
              {billingItem?.id ? 'Ändra faktureringspost' : 'Lägg till faktureringspost'}
            </Typography>
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
              sx={{ display: 'flex', flexDirection: 'column', flex: '1' }}
            >
              <Grid container spacing={2}>
                <form.Field
                  name="type"
                  validators={{
                    onChange: ({ value }) => {
                      if (!value) {
                        return 'Du måste välja en typ';
                      }
                      return undefined;
                    },
                  }}
                >
                  {(field) => (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="typeLabel">Typ</InputLabel>
                        <Select
                          labelId="typeLabel"
                          id="type"
                          value={field.state.value}
                          label="Typ"
                          onChange={(e) => field.handleChange(e.target.value as BillingItemType)}
                          error={field.state.meta.errors.length > 0}
                        >
                          {Object.entries(BillingItemType).map(([key, value]) => (
                            <MenuItem key={key} value={value}>
                              {value === BillingItemType.SUBSCRIPTION && 'Prenumeration'}
                              {value === BillingItemType.RECURRING && 'Återkommande'}
                              {value === BillingItemType.USAGE && 'Användning'}
                              {value === BillingItemType.ONE_TIME && 'Engångsavgift'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </form.Field>

                {type === BillingItemType.SUBSCRIPTION && (
                  <form.Field name="subscriptionId">
                    {(field) => (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel id="subscriptionIdLabel">Prenumeration</InputLabel>
                          <Select
                            labelId="subscriptionIdLabel"
                            id="subscriptionId"
                            value={field.state.value}
                            label="Prenumerations-ID (valfritt)"
                            onChange={(e) => field.handleChange(e.target.value as BillingItemType)}
                            error={field.state.meta.errors.length > 0}
                          >
                            <MenuItem value="">Ingen prenumeration</MenuItem>
                            {ALL_SUBSCRIPTIONS.map((subscription) => (
                              <MenuItem key={subscription} value={subscription}>
                                {t(`subscriptions.${subscription}`)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </form.Field>
                )}

                {type === BillingItemType.RECURRING && (
                  <>
                    <form.Field name="recurringType">
                      {(field) => (
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel id="recurringTypeLabel">Återkommande typ</InputLabel>
                            <Select
                              labelId="recurringTypeLabel"
                              id="recurringType"
                              value={field.state.value}
                              label="Återkommande typ"
                              onChange={(e) => field.handleChange(e.target.value)}
                            >
                              {Object.entries(RECURRING_TYPES).map(([key, value]) => (
                                <MenuItem key={key} value={value}>
                                  {t(`recurring.${value}`)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </form.Field>
                    <form.Field name="reference">
                      {(field) => (
                        <Grid item xs={12}>
                          <TextField
                            id="reference"
                            label="Referens (valfritt)"
                            variant="outlined"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            fullWidth
                          />
                        </Grid>
                      )}
                    </form.Field>
                  </>
                )}
                {type === BillingItemType.ONE_TIME && (
                  <form.Field name="name">
                    {(field) => (
                      <Grid item xs={12}>
                        <TextField
                          id="name"
                          label="Namn"
                          variant="outlined"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    )}
                  </form.Field>
                )}

                {(type === BillingItemType.RECURRING || type === BillingItemType.SUBSCRIPTION) && (
                  <>
                    <form.Field
                      name="startDate"
                      validators={{
                        onChange: ({ value }) => {
                          if (!value) {
                            return 'Du måste ange ett startdatum';
                          }
                          return undefined;
                        },
                      }}
                    >
                      {(field) => (
                        <Grid item xs={12}>
                          <DatePicker
                            sx={{ width: '100%' }}
                            onChange={(date) => field.handleChange(date ?? DateTime.now())}
                            value={field.state.value}
                            openTo="month"
                            views={['year', 'month', 'day']}
                            format="yyyy-MM-dd"
                            slotProps={{
                              textField: {
                                label: 'Startdatum',
                                helperText: field.state.meta.errors.join(', '),
                                error: field.state.meta.errors.length > 0,
                              },
                            }}
                          />
                        </Grid>
                      )}
                    </form.Field>
                    <form.Field name="endDate">
                      {(field) => (
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <DatePicker
                              sx={{ width: '100%' }}
                              onChange={(date) => field.handleChange(date)}
                              value={field.state.value}
                              openTo="month"
                              views={['year', 'month', 'day']}
                              format="yyyy-MM-dd"
                              slotProps={{
                                textField: {
                                  label: 'Slutdatum (valfritt)',
                                  helperText: field.state.meta.errors.join(', '),
                                  error: field.state.meta.errors.length > 0,
                                },
                              }}
                            />
                            {billingItemId && (
                              <Button
                                onClick={() => field.handleChange(DateTime.now())}
                                variant="contained"
                                color="error"
                              >
                                Avsluta
                              </Button>
                            )}
                          </Box>
                        </Grid>
                      )}
                    </form.Field>
                    <form.Field name="paidUntil">
                      {(field) => (
                        <Grid item xs={12}>
                          <DatePicker
                            sx={{ width: '100%' }}
                            onChange={(date) => field.handleChange(date)}
                            value={field.state.value}
                            openTo="month"
                            views={['year', 'month', 'day']}
                            format="yyyy-MM-dd"
                            slotProps={{
                              textField: {
                                label: 'Betal till (valfritt)',
                                helperText: field.state.meta.errors.join(', '),
                                error: field.state.meta.errors.length > 0,
                              },
                            }}
                          />
                        </Grid>
                      )}
                    </form.Field>
                  </>
                )}
                {type === BillingItemType.USAGE && (
                  <>
                    <form.Field name="usageType">
                      {(field) => (
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel id="usageTypeLabel">Användningstyp</InputLabel>
                            <Select
                              labelId="usageTypeLabel"
                              id="usageType"
                              value={field.state.value}
                              label="Användningstyp"
                              onChange={(e) => field.handleChange(e.target.value)}
                            >
                              {Object.entries(USAGE_TYPES).map(([key, value]) => (
                                <MenuItem key={key} value={value}>
                                  {t(`usage.${value}`)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </form.Field>
                    <form.Field name="period">
                      {(field) => (
                        <Grid item xs={12}>
                          <DatePicker
                            sx={{ width: '100%' }}
                            onChange={(date) =>
                              field.handleChange(date ? date.toFormat('yyyy-MM') : '')
                            }
                            value={
                              field.state.value
                                ? DateTime.fromFormat(field.state.value, 'yyyy-MM')
                                : DateTime.now()
                            }
                            views={['year', 'month']}
                            slotProps={{
                              textField: {
                                label: 'Period',
                                helperText: field.state.meta.errors.join(', '),
                                error: field.state.meta.errors.length > 0,
                              },
                            }}
                          />
                        </Grid>
                      )}
                    </form.Field>
                  </>
                )}

                {type === BillingItemType.ONE_TIME && (
                  <form.Field name="datePaid">
                    {(field) => (
                      <Grid item xs={12}>
                        <DatePicker
                          sx={{ width: '100%' }}
                          onChange={(date) => {
                            return field.handleChange(date?.toISO() ?? '');
                          }}
                          value={field.state.value ? DateTime.fromISO(field.state.value) : null}
                          format="dd/MM/yyyy"
                          slotProps={{
                            textField: {
                              label: 'Betaldatum',
                              helperText: field.state.meta.errors.join(', '),
                              error: field.state.meta.errors.length > 0,
                            },
                          }}
                        />
                      </Grid>
                    )}
                  </form.Field>
                )}

                <form.Field
                  name="quantity"
                  validators={{
                    onChange: ({ value }) => {
                      if (!value || value < 1) {
                        return 'Antal måste vara minst 1';
                      }
                      return undefined;
                    },
                  }}
                >
                  {(field) => (
                    <Grid item xs={12}>
                      <TextField
                        id="quantity"
                        label="Antal"
                        type="number"
                        variant="outlined"
                        value={field.state.value.toString()}
                        onChange={(e) => field.handleChange(parseInt(e.target.value, 10) || 0)}
                        inputProps={{ min: 1 }}
                        fullWidth
                        error={field.state.meta.errors.length > 0}
                        helperText={field.state.meta.errors.join(', ')}
                      />
                    </Grid>
                  )}
                </form.Field>

                {(billingItemId || type === BillingItemType.ONE_TIME) && (
                  <>
                    <form.Field name="unitPrice">
                      {(field) => (
                        <Grid item xs={8}>
                          <TextField
                            id="unitPrice"
                            label="Pris per enhet"
                            type="number"
                            variant="outlined"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            inputProps={{ step: 0.01 }}
                            fullWidth
                            error={field.state.meta.errors.length > 0}
                            helperText={field.state.meta.errors.join(', ')}
                          />
                        </Grid>
                      )}
                    </form.Field>
                    <form.Field name="currency">
                      {(field) => (
                        <Grid item xs={4}>
                          <FormControl fullWidth>
                            <InputLabel id="currencyLabel">Valuta</InputLabel>
                            <Select
                              labelId="currencyLabel"
                              id="currency"
                              value={field.state.value}
                              label="Valuta"
                              onChange={(e) => field.handleChange(e.target.value as Currency)}
                            >
                              <MenuItem value={Currency.SEK}>SEK</MenuItem>
                              <MenuItem value={Currency.EUR}>EUR</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </form.Field>
                  </>
                )}
                <form.Field
                  name="discount"
                  validators={{
                    onChange: ({ value }) => {
                      const discount = Number(value);
                      if (
                        value.trim() === '' ||
                        !Number.isFinite(discount) ||
                        discount < 0 ||
                        discount > 100
                      ) {
                        return 'Ange en rabatt mellan 0 och 100 procent';
                      }
                      return undefined;
                    },
                  }}
                >
                  {(field) => (
                    <Grid item xs={12}>
                      <TextField
                        id="discount"
                        label="Rabatt (%)"
                        type="number"
                        variant="outlined"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 'any' }}
                        required
                        fullWidth
                        error={field.state.meta.errors.length > 0}
                        helperText={field.state.meta.errors.join(', ')}
                      />
                    </Grid>
                  )}
                </form.Field>
                <form.Field name="taxRate">
                  {(field) => (
                    <Grid item xs={12}>
                      <TextField
                        id="taxRate"
                        label="Moms"
                        type="number"
                        variant="outlined"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        fullWidth
                        error={field.state.meta.errors.length > 0}
                        helperText={field.state.meta.errors.join(', ')}
                      />
                    </Grid>
                  )}
                </form.Field>
                <form.Field name="isDemo">
                  {(field) => (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={field.state.value}
                            onChange={(e) => field.handleChange(e.target.checked)}
                          />
                        }
                        label="Demo - Gratis"
                      />
                    </Grid>
                  )}
                </form.Field>
              </Grid>

              <Box
                sx={{
                  marginTop: 'auto',
                  marginLeft: 'auto',
                  padding: '1rem 0',
                  gap: 2,
                  display: 'flex',
                }}
              >
                {billingItemId && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      setDeleteDialogOpen(true);
                    }}
                  >
                    Ta bort
                  </Button>
                )}
                <Button variant="contained" type="submit" disabled={isLoading}>
                  {isLoading ? 'Sparar...' : 'Spara'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Drawer>
        {billingItemId && (
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Raden kommer att permanent raderas, och kommer inte komma med på framtida fakturor.
                Är du säker på att du inte bara ska sätta en slutdatum?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  deleteBillingItem(companyId, billingItemId)
                    .then(() => {
                      onClose();
                    })
                    .catch(() => {});
                }}
                color="error"
                variant="contained"
                disabled={isDeleteLoading}
              >
                {t('paymethodScreen.yes')}
              </Button>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                variant="contained"
                autoFocus
                disabled={isDeleteLoading}
              >
                {t('paymethodScreen.no')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  },
);

export default BillingItemForm;
